import { motion } from 'framer-motion';
import { 
  Sparkles, 
  Calendar, 
  MapPin, 
  Route, 
  Globe, 
  Search 
} from 'lucide-react';
import { ToolCallCard } from '../../lib/hooks/useToolCallHandling';
import { cn } from '@city-sage/aurum-avis-labs-components';

interface ToolCallIndicatorProps {
  toolCall: ToolCallCard;
}

const getToolCallIcon = (type: ToolCallCard['type']) => {
  switch (type) {
    case 'create_itinerary':
    case 'update_itinerary':
      return Sparkles;
    case 'add_day':
      return Calendar;
    case 'add_stop':
    case 'update_stop':
    case 'lookup_location':
      return MapPin;
    case 'add_leg':
    case 'optimize_route':
      return Route;
    case 'web_browser':
      return Globe;
    case 'google_search':
      return Search;
    default:
      return Sparkles;
  }
};

const getToolCallLabel = (type: ToolCallCard['type']) => {
  switch (type) {
    case 'create_itinerary':
      return 'Creating itinerary...';
    case 'update_itinerary':
      return 'Updating itinerary...';
    case 'add_day':
      return 'Adding day...';
    case 'add_stop':
      return 'Adding stop...';
    case 'add_leg':
      return 'Adding route...';
    case 'update_stop':
      return 'Updating stop...';
    case 'optimize_route':
      return 'Optimizing route...';
    case 'web_browser':
      return 'Browsing web...';
    case 'google_search':
      return 'Searching...';
    case 'lookup_location':
      return 'Looking up location...';
    default:
      return 'Processing...';
  }
};

export function ToolCallIndicator({ toolCall }: ToolCallIndicatorProps) {
  const Icon = getToolCallIcon(toolCall.type);
  const label = getToolCallLabel(toolCall.type);

  return (
    <motion.div
      className={cn(
        "flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-2.5 rounded-lg bg-primary/10 border border-primary/20",
        "text-sm md:text-base text-foreground"
      )}
      animate={{
        opacity: [0.6, 1, 0.6],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <Icon className="h-4 w-4 md:h-5 md:w-5 text-primary flex-shrink-0" />
      <span className="truncate font-medium">{label}</span>
    </motion.div>
  );
}

// Simple inline text version for use in headers
export function ToolCallIndicatorText({ toolCall }: ToolCallIndicatorProps) {
  const Icon = getToolCallIcon(toolCall.type);
  const label = getToolCallLabel(toolCall.type);

  return (
    <motion.span
      className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-muted-foreground"
      animate={{
        opacity: [0.6, 1, 0.6],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <Icon className="h-3 w-3 md:h-3.5 md:w-3.5 text-primary flex-shrink-0" />
      <span className="truncate font-medium">{label}</span>
    </motion.span>
  );
}

